import type { AutomationEntityType, TextObject } from '@awork/features/project/models/automation.model'
import {
  type AutomationCustomFieldComparison,
  type AutomationCustomFieldComparisonMode,
  type AutomationCustomFieldDefinitionValue,
  type AutomationCustomFieldValuePayload,
  type AutomationStatusAggregationMode,
  type AutomationTaskDateProperty,
  type AutomationTaskParentSource,
  type AutomationTaskScope,
  type AutomationTaskTargetScope,
  AutomationValueType
} from '@awork/features/project/models/automation.model'
import type {
  AutomationActionValue,
  AutomationActionValueType,
  AutomationRelativeDate,
  IAutomationAction
} from '@awork/features/project/models/automation-action.model'
import type {
  AutomationTriggerValue,
  AutomationTriggerValueType,
  IAutomationTrigger
} from '@awork/features/project/models/automation-trigger.model'
import isTest from '@awork/_shared/functions/is-test'
import replace from '@awork/_shared/functions/replace'
import { escapeHtml } from '@awork/_shared/functions/string-operations'
import { AutomationTask } from '@awork/features/project/models/automation-task.model'
import { ChecklistItem } from '@awork/features/task/models/checklist-item'
import { replaceTestNoOp, translationsTestProxy } from '@awork/_shared/functions/translation'
import { AutomationRelatedUser } from '@awork/features/project/models/automation-related-user.model'
import { UserQuery } from '@awork/features/user/state/user.query'
import { CompanyQuery } from '@awork/features/company/state/company.query'
import { CustomFieldDefinitionQuery } from '@awork/features/project/state/custom-field-definition.query'
import {
  customFieldComparisonRequiresValue,
  getCustomFieldComparisonLabel,
  getCustomFieldComparisonMode,
  getCustomFieldComparisonModeLabel,
  getCustomFieldValuePlaceholder,
  isScalarCustomFieldComparisonType,
  shouldShowCustomFieldComparisonDetail
} from '@awork/features/project/models/automation-custom-field-comparison.helper'

function shouldUseRuntimeReplacement(): boolean {
  return !!globalThis.q?.translations || !isTest()
}

export function translate(msg: string, params: Record<string, string | number>): string {
  return shouldUseRuntimeReplacement() ? replace(msg, params) : replaceTestNoOp(msg, params)
}

type SelectionTranslations = Record<string, string | Record<string, string> | undefined> & {
  list?: Record<string, string>
}

/**
 * Gets the action translations and the trigger translations
 * @returns {{ actionTranslations; triggerTranslations; }}
 */
function getTranslations(): { actionTranslations; triggerTranslations } {
  const translations = globalThis.q?.translations ?? (isTest() ? translationsTestProxy() : q.translations)
  const { AutomationActionModel: actionTranslations, AutomationTriggerModel: triggerTranslations } = translations

  return { actionTranslations, triggerTranslations }
}

function getSelectionTranslationText(selectionKey: string, translations: SelectionTranslations): string {
  const listTranslation = translations.list?.[selectionKey]

  if (typeof listTranslation === 'string' && listTranslation.length > 0) {
    return listTranslation
  }

  const baseTranslation = translations[selectionKey]

  return typeof baseTranslation === 'string' ? baseTranslation : String(baseTranslation ?? '')
}

/**
 * Gets the action's or triggers text based on its type (placeholder text used for the selection list)
 * @param {string} actionKey
 * @param {'action' | 'trigger'} type
 * @returns {string}
 */
export function getSelectionText(actionKey: string, type: 'action' | 'trigger'): string {
  const { actionTranslations, triggerTranslations } = getTranslations()
  const translationText = getSelectionTranslationText(
    actionKey,
    (type === 'action' ? actionTranslations : triggerTranslations) as SelectionTranslations
  )
  const placeholders = findPlaceholders(translationText)

  if (placeholders.length > 0) {
    const translationValues = {}

    placeholders.forEach(p => {
      translationValues[p] = getValueText(
        {
          name: p as AutomationValueType,
          value: undefined,
          id: undefined,
          actionId: undefined
        },
        true,
        false,
        false
      )
    })

    return type === 'trigger'
      ? translate(translationText, translationValues) + '...'
      : '...' + translate(translationText, translationValues)
  } else {
    return type === 'trigger' ? translationText + '...' : '...' + translationText
  }
}

/**
 * Finds all placeholders in a translation as an array
 * @param {string} text
 * @returns {string[]}
 */
export function findPlaceholders(text: string): string[] {
  const placeholders = []

  // TODO dont know exactly why, but the test was failing here
  if (text && typeof text.substring === 'function') {
    let processedText = text.substring(0)

    do {
      const startPlaceholder = processedText.search('{{')
      const endPlaceholder = processedText.substring(startPlaceholder).search('}}') + startPlaceholder

      if (startPlaceholder !== -1 && endPlaceholder !== -1) {
        placeholders.push(processedText.substring(startPlaceholder + 2, endPlaceholder))
        processedText = processedText.substring(endPlaceholder + 2)
      }
    } while (processedText.search('{{') !== -1)
  }

  return placeholders
}

/**
 * Returns display value for action or trigger value
 * @param {AutomationActionValue | AutomationTriggerValue} value
 * @param {boolean} alternateCase
 * @returns {string}
 */
export function getValue(value: AutomationActionValue | AutomationTriggerValue, alternateCase: boolean): string {
  const { actionTranslations } = getTranslations()

  if (!value.value) {
    return getPlaceholderValue(value)
  }

  switch (value.name) {
    case AutomationValueType.name:
    case AutomationValueType.hours:
    case AutomationValueType.tag:
    case AutomationValueType.text:
    case AutomationValueType.prompt:
      return value.value as string
    case AutomationValueType.unit: {
      const unitValue = value.value as string

      return actionTranslations.AutomationRelativeDate[unitValue] || unitValue
    }
    case AutomationValueType.status:
    case AutomationValueType.statusFrom:
    case AutomationValueType.statusTo:
    case AutomationValueType.list:
    case AutomationValueType.user:
    case AutomationValueType.project:
    case AutomationValueType.type:
    case AutomationValueType.taskBundle:
    case AutomationValueType.agent: {
      const key = (value.value as { key?: string }).key

      if (key) {
        const relatedUser = new AutomationRelatedUser(key)

        if (alternateCase) {
          return relatedUser.getNameForThenCase()
        } else {
          return relatedUser.getName()
        }
      } else {
        return (value.value as AutomationEntityType).name
      }
    }
    case AutomationValueType.projectRole:
      return q.translations.AutomationActionModel.withRole + (value.value as AutomationEntityType).name
    case AutomationValueType.amount:
    case AutomationValueType.days:
      return (value.value as number).toString()
    case AutomationValueType.date: {
      const relativeDate = value.value as AutomationRelativeDate

      return (
        q.translations.DateSelectorComponent[relativeDate.date] +
        ' + ' +
        relativeDate.offset +
        ' ' +
        actionTranslations.AutomationRelativeDate[relativeDate.unit]
      )
    }
    case AutomationValueType.checklistItems: {
      const checklistItems = value.value as ChecklistItem[]
      const count = checklistItems.length ? `${checklistItems.length} ` : ''

      return (
        count +
        (checklistItems.length === 1 ? actionTranslations.checklistItem : actionTranslations.placeholder.checklistItems)
      )
    }
    case AutomationValueType.customField:
      return getCustomFieldDefinitionName(value.value as AutomationCustomFieldDefinitionValue)
    case AutomationValueType.customFieldComparisonMode:
      return getCustomFieldComparisonModeLabel(value.value as AutomationCustomFieldComparisonMode)
    case AutomationValueType.customFieldComparison:
      return getCustomFieldComparisonLabel(value.value as AutomationCustomFieldComparison)
    case AutomationValueType.customFieldValue:
      return getCustomFieldValueLabel(value.value as AutomationCustomFieldValuePayload)
    case AutomationValueType.taskScope:
      return getTaskScopeLabel(value.value as AutomationTaskScope)
    case AutomationValueType.statusAggregation:
      return getStatusAggregationLabel(value.value as AutomationStatusAggregationMode)
    case AutomationValueType.property:
      return getDatePropertyLabel(value.value as AutomationTaskDateProperty)
    case AutomationValueType.targetScope:
      return getTargetScopeLabel(value.value as AutomationTaskTargetScope)
    case AutomationValueType.parentSource:
      return getParentSourceLabel(value.value as AutomationTaskParentSource)
    default:
      return getPlaceholderValue(value)
  }
}

/**
 * Returns translated placeholder for action or trigger value
 * @param {AutomationActionValue | AutomationTriggerValue} value
 * @returns {string}
 */
export function getPlaceholderValue(value: AutomationActionValue | AutomationTriggerValue): string {
  if (value.name === AutomationValueType.customFieldComparisonMode) {
    return q.translations.AutomationActionModel.placeholder.customFieldComparisonMode
  }

  return q.translations.AutomationActionModel.placeholder[value.name]
}

/**
 * Returns html for an action or trigger value. Values are escaped because they can contain user input.
 * @param {AutomationActionValue | AutomationTriggerValue} actionValue
 * @param {boolean} usePlaceholderValue Use placeholders instead the values
 * @param {boolean} useSpan use span as placeholder, otherwise use <b>
 * @param {boolean} alternateCase use alternate case for the value
 * @returns {string}
 */
export function getValueText(
  actionValue: AutomationActionValue | AutomationTriggerValue,
  usePlaceholderValue: boolean,
  useSpan: boolean,
  alternateCase: boolean
): string {
  const rawValue = usePlaceholderValue ? getPlaceholderValue(actionValue) : getValue(actionValue, alternateCase)
  const value = escapeHtml(String(rawValue ?? ''))

  if (useSpan) {
    return '<span data-placeholder="' + actionValue.name + '">' + value + '</span>'
  } else {
    let placeholder = ''

    if (
      [
        AutomationValueType.tag,
        AutomationValueType.list,
        AutomationValueType.type,
        AutomationValueType.name,
        AutomationValueType.text
      ].includes(actionValue.name)
    ) {
      placeholder = escapeHtml(String(getPlaceholderValue(actionValue) ?? '')) + ' '
    }

    return usePlaceholderValue ? '<b>' + value + '</b>' : '<b>' + placeholder + value + '</b>'
  }
}

/**
 * Creates and array of objects based on the trigger's or action's translation to be used in a template to render
 * text and AutomationInlineValueComponents inline
 * @param {IAutomationTrigger | IAutomationAction} item
 * @param {'trigger' | 'action'} type
 * @returns {TextObject[]}
 */
export function getTextObject(item: IAutomationTrigger | IAutomationAction, type: 'trigger' | 'action'): TextObject[] {
  if (!item) {
    return []
  }

  const { actionTranslations, triggerTranslations } = getTranslations()
  const translationText = type === 'trigger' ? triggerTranslations[item.type] : actionTranslations[item.type]

  if (translationText && typeof translationText === 'string') {
    const translationArray = getTextAndPlaceholders(translationText)
    const itemValues: (AutomationTriggerValue | AutomationActionValue)[] = item.values
    const itemPlaceholderNames = itemValues?.map(value => value.name) || []

    return translationArray
      .map(text => {
        const isPlaceholder =
          itemPlaceholderNames.includes(text as AutomationValueType) ||
          text === AutomationValueType.customFieldComparisonMode

        if (isPlaceholder) {
          return createAutomationValue(text, itemValues, type, item.type)
        }

        return { type: 'text', value: text } as TextObject
      })
      .filter(textObject => !(textObject.type === 'text' && textObject.value === ''))
  }

  return []
}

/**
 * Creates an object representing an automation's items placeholder
 * @param {string} translationText
 * @param {(AutomationTriggerValue | AutomationActionValue)[]} itemValues
 * @param {'trigger' | 'action'} type
 * @param {string} automationItemType
 * @returns {TextObject}
 */
function createAutomationValue(
  translationText: string,
  itemValues: (AutomationTriggerValue | AutomationActionValue)[],
  type: 'trigger' | 'action',
  automationItemType: string
): TextObject {
  const customFieldDefinition = itemValues.find(value => value.name === AutomationValueType.customField)?.value as
    AutomationCustomFieldDefinitionValue | undefined
  const customFieldComparison = itemValues.find(value => value.name === AutomationValueType.customFieldComparison)
    ?.value as AutomationCustomFieldComparison | undefined
  const itemValue =
    itemValues.find(val => val.name === translationText) ??
    (translationText === AutomationValueType.customFieldComparisonMode
      ? ({
          name: AutomationValueType.customFieldComparisonMode,
          value: isScalarCustomFieldComparisonType(customFieldDefinition?.type)
            ? getCustomFieldComparisonMode(customFieldDefinition?.type, customFieldComparison)
            : ''
        } as AutomationTriggerValue | AutomationActionValue)
      : undefined)

  if (!itemValue) {
    return {
      type: 'text',
      value: ''
    }
  }

  const customFieldComparisonMode = itemValues.find(
    value => value.name === AutomationValueType.customFieldComparisonMode
  )?.value as AutomationCustomFieldComparisonMode | undefined
  const { value, entityName } = getItemValue(itemValue.value, itemValue.name, type === 'action', {
    customFieldDefinition,
    customFieldComparison,
    customFieldComparisonMode,
    automationItemType
  })

  if (
    shouldHideCustomFieldPlaceholder(
      itemValue.name,
      customFieldDefinition,
      customFieldComparison,
      customFieldComparisonMode
    )
  ) {
    return {
      type: 'text',
      value: ''
    }
  }

  return {
    type: 'placeholder',
    name: itemValue.name,
    value,
    entityName
  }
}

function shouldHideCustomFieldPlaceholder(
  itemName: AutomationValueType,
  customFieldDefinition: AutomationCustomFieldDefinitionValue | undefined,
  customFieldComparison: AutomationCustomFieldComparison | undefined,
  customFieldComparisonMode: AutomationCustomFieldComparisonMode | undefined
): boolean {
  if (itemName === AutomationValueType.customFieldComparisonMode) {
    return !isScalarCustomFieldComparisonType(customFieldDefinition?.type)
  }

  if (itemName === AutomationValueType.customFieldValue) {
    return !customFieldComparisonRequiresValue(customFieldDefinition?.type, customFieldComparison)
  }

  return (
    itemName === AutomationValueType.customFieldComparison &&
    isScalarCustomFieldComparisonType(customFieldDefinition?.type) &&
    !shouldShowCustomFieldComparisonDetail(customFieldDefinition?.type, customFieldComparisonMode)
  )
}

/**
 * Splits texts and placeholders from the string
 * @param {string} translationText
 * @returns {string[]}
 */
function getTextAndPlaceholders(translationText: string): string[] {
  return translationText.split(/[{{}}]/).filter(text => !!text)
}

/**
 * Gets the item's value and entityName (text value)
 * @param {AutomationTriggerValueType | AutomationActionValueType} itemValue
 * @param {AutomationValueType} itemName
 * @param {boolean} alternateCase
 * @param {{ customFieldDefinition?: AutomationCustomFieldDefinitionValue; customFieldComparison?: AutomationCustomFieldComparison; customFieldComparisonMode?: AutomationCustomFieldComparisonMode; automationItemType?: string; }} context
 * @returns {{ value: AutomationTriggerValueType | AutomationActionValueType; entityName: string; }}
 */
function getItemValue(
  itemValue: AutomationTriggerValueType | AutomationActionValueType,
  itemName: AutomationValueType,
  alternateCase: boolean,
  context?: {
    customFieldDefinition?: AutomationCustomFieldDefinitionValue
    customFieldComparison?: AutomationCustomFieldComparison
    customFieldComparisonMode?: AutomationCustomFieldComparisonMode
    automationItemType?: string
  }
): { value: AutomationTriggerValueType | AutomationActionValueType; entityName: string } {
  const { actionTranslations } = getTranslations()

  if (itemValue == null) {
    return { value: undefined, entityName: undefined }
  }

  // Consume type guards to extract values and names
  if (typeof itemValue === 'string' || typeof itemValue === 'number') {
    const taskScopeSentenceName = getTaskScopeSentenceName(itemName, itemValue)

    return {
      value: itemValue,
      entityName:
        taskScopeSentenceName ??
        getItemValueName(itemName, itemValue, context?.customFieldDefinition, context?.customFieldComparisonMode)
    }
  }

  if (isAutomationEntityType(itemValue)) {
    return {
      value: itemValue.id,
      entityName: itemValue.name
    }
  }

  if (isAutomationCustomFieldDefinitionValue(itemValue)) {
    return {
      value: itemValue,
      entityName: getCustomFieldDefinitionName(itemValue)
    }
  }

  if (isAutomationCustomFieldValuePayload(itemValue)) {
    return {
      value: itemValue,
      entityName: getCustomFieldValueLabel(itemValue, context?.customFieldDefinition, context?.customFieldComparison)
    }
  }

  if (isAutomationTask(itemValue)) {
    return {
      value: itemValue,
      entityName: itemValue.name
    }
  }

  if (isAutomationRelatedUser(itemValue)) {
    const relatedUser = new AutomationRelatedUser(itemValue.key)

    return {
      value: relatedUser,
      entityName: alternateCase ? relatedUser.getNameForThenCase() : relatedUser.getName()
    }
  }

  if (isAutomationRelativeDate(itemValue)) {
    return {
      value: itemValue,
      entityName: `${q.translations.DateSelectorComponent[itemValue.date]} +${itemValue.offset} ${
        actionTranslations.AutomationRelativeDate[itemValue.unit]
      }`
    }
  }

  if (areChecklistItems(itemValue)) {
    const count = itemValue.length ? `${itemValue.length} ` : ''

    return {
      value: itemValue,
      entityName:
        count +
        (itemValue.length === 1 ? actionTranslations.checklistItem : actionTranslations.placeholder.checklistItems)
    }
  }

  return { value: undefined, entityName: undefined }
}

/**
 * Returns the task-scope label used inside trigger sentences.
 * @param {AutomationValueType} itemName The placeholder name.
 * @param {string | number} itemValue The placeholder value.
 * @returns {string | undefined} The sentence-specific task-scope label when applicable.
 */
function getTaskScopeSentenceName(itemName: AutomationValueType, itemValue: string | number): string | undefined {
  if (itemName !== AutomationValueType.taskScope) {
    return undefined
  }

  return q.translations.AutomationActionModel.taskScopeStatusChanged[itemValue as AutomationTaskScope]
}

/**
 * Gets the item value's name for displaying purposes
 * @param {AutomationValueType} itemName
 * @param {string | number} itemValue
 * @param {AutomationCustomFieldDefinitionValue} customFieldDefinition
 * @param {AutomationCustomFieldComparisonMode} customFieldComparisonMode
 * @returns {string}
 */
function getItemValueName(
  itemName: AutomationValueType,
  itemValue: string | number,
  customFieldDefinition?: AutomationCustomFieldDefinitionValue,
  customFieldComparisonMode?: AutomationCustomFieldComparisonMode
): string {
  let name: string

  switch (itemName) {
    case AutomationValueType.message:
      name = q.translations.AutomationActionModel.placeholder[itemName]
      break
    case AutomationValueType.unit:
      name = q.translations.AutomationActionModel.AutomationRelativeDate[itemValue]
      break
    case AutomationValueType.taskScope:
      name = q.translations.AutomationActionModel.taskScope[itemValue]
      break
    case AutomationValueType.customFieldComparisonMode:
      name = getCustomFieldComparisonModeLabel(itemValue as AutomationCustomFieldComparisonMode, customFieldDefinition)
      break
    case AutomationValueType.customFieldComparison:
      name = getCustomFieldComparisonLabel(
        itemValue as AutomationCustomFieldComparison,
        customFieldDefinition,
        shouldShowCustomFieldComparisonDetail(customFieldDefinition?.type, customFieldComparisonMode)
      )
      break
    case AutomationValueType.statusAggregation:
      name = q.translations.AutomationActionModel.statusAggregation[itemValue]
      break
    case AutomationValueType.property:
      name = q.translations.AutomationActionModel.dateProperty[itemValue]
      break
    case AutomationValueType.targetScope:
      name = q.translations.AutomationActionModel.targetScope[itemValue]
      break
    case AutomationValueType.parentSource:
      name = q.translations.AutomationActionModel.parentSource[itemValue]
  }

  return name
}

// Type guards
export function isAutomationEntityType(value: unknown): value is AutomationEntityType {
  return (value as { name?: unknown; key?: unknown })?.name && !(value as { name?: unknown; key?: unknown })?.key
}

function isAutomationTask(value: unknown): value is AutomationTask {
  return value instanceof AutomationTask
}

function isAutomationRelatedUser(value: unknown): value is AutomationRelatedUser {
  return !!(value as { key?: unknown }).key
}

function isAutomationRelativeDate(value: unknown): value is AutomationRelativeDate {
  return !!(
    (value as { date?: unknown; offset?: unknown; unit?: unknown }).date &&
    (value as { date?: unknown; offset?: unknown; unit?: unknown }).offset &&
    (value as { date?: unknown; offset?: unknown; unit?: unknown }).unit
  )
}

function areChecklistItems(value: unknown): value is ChecklistItem[] {
  return Array.isArray(value) && (value.length === 0 || value[0] instanceof ChecklistItem)
}

function isAutomationCustomFieldDefinitionValue(value: unknown): value is AutomationCustomFieldDefinitionValue {
  return typeof value === 'object' && value !== null && 'id' in value
}

function isAutomationCustomFieldValuePayload(value: unknown): value is AutomationCustomFieldValuePayload {
  return typeof value === 'object' && value !== null && 'customFieldDefinitionId' in value
}

function getCustomFieldDefinitionName(value?: AutomationCustomFieldDefinitionValue): string {
  if (!value?.id) {
    return q.translations.AutomationActionModel.placeholder.customField
  }

  const storedDefinition = CustomFieldDefinitionQuery.instance?.getEntity(value.id)

  return value.name || storedDefinition?.name || q.translations.AutomationActionModel.deletedField
}

function getTaskScopeLabel(value: AutomationTaskScope): string {
  return q.translations.AutomationActionModel.taskScope[value]
}

/**
 * Removes bold formatting from the first rendered task-scope value in preview/display text.
 * @param {string} text Rendered automation text.
 * @param {AutomationTaskScope} [value='any'] Task-scope value to normalize.
 * @returns {string} Text with the first matching task-scope label rendered plainly.
 */
export function renderHiddenTaskScopeAsPlainText(text: string, value: AutomationTaskScope = 'any'): string {
  const taskScopeLabel = escapeHtml(getTaskScopeLabel(value))

  return text.replace(`<b>${taskScopeLabel}</b>`, taskScopeLabel)
}

function getStatusAggregationLabel(value: AutomationStatusAggregationMode): string {
  return q.translations.AutomationActionModel.statusAggregation[value]
}

function getDatePropertyLabel(value: AutomationTaskDateProperty): string {
  return q.translations.AutomationActionModel.dateProperty[value]
}

function getTargetScopeLabel(value: AutomationTaskTargetScope): string {
  return q.translations.AutomationActionModel.targetScope[value]
}

function getParentSourceLabel(value: AutomationTaskParentSource): string {
  return q.translations.AutomationActionModel.parentSource[value]
}

function getCustomFieldValueLabel(
  value: AutomationCustomFieldValuePayload | undefined,
  definition?: AutomationCustomFieldDefinitionValue,
  comparison?: AutomationCustomFieldComparison
): string {
  const storedDefinition = value?.customFieldDefinitionId
    ? CustomFieldDefinitionQuery.instance?.getEntity(value.customFieldDefinitionId)
    : undefined
  const resolvedDefinition = definition ?? (storedDefinition as AutomationCustomFieldDefinitionValue | undefined)

  if (!value) {
    return getCustomFieldValuePlaceholder(resolvedDefinition)
  }

  const primitiveValueLabel = getPrimitiveCustomFieldValueLabel(value)

  if (primitiveValueLabel) {
    return primitiveValueLabel
  }

  const referencedValueLabel = getReferencedCustomFieldValueLabel(value, storedDefinition)

  if (referencedValueLabel) {
    return referencedValueLabel
  }

  if (customFieldComparisonRequiresValue(resolvedDefinition?.type, comparison)) {
    return getCustomFieldValuePlaceholder(resolvedDefinition)
  }

  return ''
}

function getPrimitiveCustomFieldValueLabel(value: AutomationCustomFieldValuePayload): string | undefined {
  if (value.textValue) {
    return value.textValue
  }

  if (value.numberValue != null) {
    return value.numberValue.toString()
  }

  if (value.dateValue) {
    return new Date(value.dateValue).toLocaleDateString()
  }

  if (value.booleanValue != null) {
    return value.booleanValue
      ? q.translations.AutomationActionModel.customFieldBoolean.true
      : q.translations.AutomationActionModel.customFieldBoolean.false
  }

  return undefined
}

function getReferencedCustomFieldValueLabel(
  value: AutomationCustomFieldValuePayload,
  storedDefinition: { selectionOptions?: { id: string; value: string }[] } | undefined
): string | undefined {
  if (value.userIdValue) {
    const userQuery = UserQuery.instance?.queryUser(value.userIdValue)
    const user = userQuery?.()

    return user?.fullName || q.translations.AutomationActionModel.deletedUser
  }

  if (value.clientIdValue) {
    const companyQuery = CompanyQuery.instance?.queryCompany(value.clientIdValue)
    const company = companyQuery?.()

    return company?.name || q.translations.AutomationActionModel.deletedClient
  }

  if (value.selectionOptionIdValue) {
    const selectionOption = storedDefinition?.selectionOptions?.find(
      option => option.id === value.selectionOptionIdValue
    )

    return selectionOption?.value || q.translations.AutomationActionModel.deletedOption
  }

  return undefined
}
